// Updated SessionTimeoutWarning.jsx with better ref handling

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Modal, Button, Progress } from 'antd';
import sessionTimeoutManager from '../utils/sessionTimeoutManager';

// Important: Use forwardRef to properly expose methods to parent
const SessionTimeoutWarning = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState({
    minutes: 0,
    seconds: 0,
    total: 0
  });
  const [timer, setTimer] = useState(null);

  // Primary button style consistent with your app theme
  const primaryButtonStyle = {
    background: '#394A3F',
    borderColor: '#394A3F',
    color: 'white',
    fontWeight: 300,
    height: 40,
    padding: '0 20px',
    borderRadius: 0,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    border: 'none',
    boxShadow: 'none'
  };

  // Start countdown when modal becomes visible
  useEffect(() => {
    if (visible) {
      console.log('Warning modal is now visible');
      
      // Update the timer every second
      const intervalId = setInterval(() => {
        const remaining = sessionTimeoutManager.getTimeRemaining();
        
        setTimeRemaining(remaining);
        console.log('Time remaining:', remaining);
        
        // Close modal and stop timer if we've logged out or manually extended
        if (remaining.total <= 0 || !visible) {
          setVisible(false);
          clearInterval(intervalId);
        }
      }, 1000);
      
      setTimer(intervalId);
      
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [visible]);

  // Clear the timer when component unmounts
  useEffect(() => {
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [timer]);

  // Format the time as MM:SS
  const formatTime = () => {
    const { minutes, seconds } = timeRemaining;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage for the progress bar
  const calculateProgress = () => {
    // This should be adjusted to match the actual warning time 
    // For 1 minute warning, use 60000ms
    const warningTime = 60 * 1000;
    return (timeRemaining.total / warningTime) * 100;
  };

  // Handler for continuing the session
  const handleContinue = () => {
    console.log('User clicked continue');
    // Reset the activity timer
    sessionTimeoutManager.resetTimer();
    
    // Hide the modal
    setVisible(false);
  };

  // Handler for logging out
  const handleLogout = async () => {
    console.log('User clicked logout');
    setVisible(false);
    await sessionTimeoutManager.handleTimeout();
  };

  // Expose methods to parent component using useImperativeHandle
  useImperativeHandle(ref, () => ({
    showModal: () => {
      console.log('showModal called');
      setVisible(true);
    },
    hideModal: () => {
      console.log('hideModal called');
      setVisible(false);
    }
  }));

  return (
    <Modal
      title="Session Timeout Warning"
      open={visible}
      onCancel={handleContinue}
      footer={null}
      closable={false}
      maskClosable={false}
      style={{ top: 20 }}
      width={400}
    >
      <div style={{ textAlign: 'center' }}>
        <h3>Your session is about to expire</h3>
        
        <p>
          Due to inactivity, your session will expire in:
        </p>
        
        <div style={{ margin: '20px 0' }}>
          <Progress 
            type="circle" 
            percent={Math.round(calculateProgress())} 
            format={() => formatTime()}
            strokeColor="#394A3F"
          />
        </div>
        
        <p>
          Would you like to continue your session?
        </p>
        
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleLogout}
            style={{ ...primaryButtonStyle, background: '#8A9B8F' }}
          >
            LOG OUT
          </Button>
          
          <Button 
            type="primary" 
            onClick={handleContinue}
            style={primaryButtonStyle}
          >
            CONTINUE SESSION
          </Button>
        </div>
      </div>
    </Modal>
  );
});

export default SessionTimeoutWarning;